import { getAdminToken } from "@/lib/adminToken";
import { useState, useEffect } from "react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star,
  Search,
  CheckCircle2,
  XCircle,
  Trash2,
  Plus,
  X,
  Clock,
  Calendar,
  User,
  Mail,
  Loader2,
  Eye,
  Filter,
  Pencil,
} from "lucide-react";

const API_BASE = "/api";

const sourceOptions = [
  { value: "website", label: "Website" },
  { value: "google", label: "Google" },
];

interface Review {
  id: number;
  name: string;
  email: string;
  rating: number;
  comment: string;
  status: string;
  source: string;
  created_at: string;
}

const sourceColors: Record<string, string> = {
  google: "bg-blue-100 text-blue-700 border-blue-200",
  website: "bg-purple-100 text-purple-700 border-purple-200",
};

const sourceLabels: Record<string, string> = {
  google: "Google",
  website: "Website",
};

const statusColors: Record<string, string> = {
  approved: "bg-green-100 text-green-700 border-green-200",
  pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
  draft: "bg-neutral-100 text-neutral-600 border-neutral-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

const statusLabels: Record<string, string> = {
  approved: "Approved",
  pending: "Pending",
  draft: "Draft",
  rejected: "Rejected",
};

export default function AdminReviews() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [syncingGoogle, setSyncingGoogle] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [showNotification, setShowNotification] = useState<string | null>(null);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const { user } = useAdminAuth();

  // Form state for adding review
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    rating: 5,
    comment: "",
    status: "approved",
    source: "website",
    created_at: new Date().toISOString().slice(0, 16), // datetime-local format
  });
  const [formSubmitting, setFormSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/reviews?status=all`, { headers: { Authorization: `Bearer ${getAdminToken()}` } });
      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || "Failed to fetch reviews");
      }

      if (!Array.isArray(data)) {
        throw new Error("Invalid response format");
      }

      setReviews(data);
    } catch (err) {
      console.error("Fetch error:", err);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredReviews = reviews.filter((review) => {
    const matchesSearch =
      review.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      review.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      review.comment?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "All" || review.status === statusFilter;
    const matchesSource = sourceFilter === "All" || review.source === sourceFilter;
    return matchesSearch && matchesStatus && matchesSource;
  });

  const stats = {
    total: reviews.length,
    approved: reviews.filter((r) => r.status === "approved").length,
    pending: reviews.filter((r) => r.status === "pending").length,
    rejected: reviews.filter((r) => r.status === "rejected").length,
    google: reviews.filter((r) => r.source === "google").length,
    website: reviews.filter((r) => r.source === "website").length,
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      const response = await fetch(`${API_BASE}/reviews/${id}`, {
        method: "PUT",
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to update review");
      }

      setReviews((current) => current.map((review) => review.id === id ? { ...review, status: newStatus } : review));
      setShowNotification(newStatus === "approved" ? "Review approved" : newStatus === "draft" ? "Review moved to draft" : "Review updated");
      setTimeout(() => setShowNotification(null), 3000);
    } catch (error) {
      setShowNotification(error instanceof Error ? error.message : "Failed to update review");
      setTimeout(() => setShowNotification(null), 3000);
    }
  };

  const openEditReview = (review: Review) => {
    setEditingReview(review);
    setFormData({
      name: review.name,
      email: review.email || "",
      rating: review.rating,
      comment: review.comment,
      status: review.status,
      source: review.source,
      created_at: review.created_at ? new Date(review.created_at).toISOString().slice(0, 16) : "",
    });
    setShowAddModal(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE}/reviews/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      });

      if (!response.ok) {
        throw new Error(`Delete failed: HTTP ${response.status}`);
      }

      setReviews(reviews.filter((r) => r.id !== id));
      setShowDeleteConfirm(null);
      setSelectedReview(null);
      setShowNotification("Review deleted");
      setTimeout(() => setShowNotification(null), 3000);
    } catch {
      setShowNotification("Failed to delete review");
      setTimeout(() => setShowNotification(null), 3000);
    }
  };

  const handleAddReview = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);

    try {
      const response = await fetch(editingReview ? `${API_BASE}/reviews/${editingReview.id}` : `${API_BASE}/reviews`, {
        method: editingReview ? "PUT" : "POST",
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
        body: JSON.stringify({
          ...formData,
          created_at: formData.created_at
            ? new Date(formData.created_at).toISOString()
            : undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (editingReview ? "Failed to update review" : "Failed to add review"));
      }

      setShowAddModal(false);
      setEditingReview(null);
      setFormData({
        name: "",
        email: "",
        rating: 5,
        comment: "",
        status: "approved",
        source: "website",
        created_at: new Date().toISOString().slice(0, 16),
      });
      setShowNotification(editingReview ? "Review updated successfully" : "Review added successfully");
      setTimeout(() => setShowNotification(null), 3000);
      await fetchReviews();
    } catch (err) {
      setShowNotification(err instanceof Error ? err.message : "Failed to add review");
      setTimeout(() => setShowNotification(null), 3000);
    } finally {
      setFormSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-NG", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div>
      {/* Notification */}
      <AnimatePresence>
        {showNotification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 right-4 z-50 bg-primary text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm"
          >
            <CheckCircle2 className="w-4 h-4" />
            {showNotification}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-8 h-0.5 bg-accent rounded-full" />
              <span className="text-accent text-xs font-medium uppercase tracking-wide">Testimonials</span>
            </div>
            <h1 className="text-2xl font-bold text-primary">Reviews</h1>
            <p className="text-neutral-400 text-sm mt-1">
              Manage customer reviews and testimonials
            </p>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "super-admin" && (
              <button
                onClick={async () => {
                setSyncingGoogle(true);
                try {
                  const res = await fetch(`${API_BASE}/reviews`, {
                    method: "POST",
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
                    body: JSON.stringify({ action: "sync-google" })
                  });
                  const data = await res.json();
                  if (data.success) {
                    setShowNotification(data.message);
                    await fetchReviews();
                  } else {
                    setShowNotification(data.error || "Sync failed");
                  }
                } catch {
                  setShowNotification("Failed to sync Google reviews");
                } finally {
                  setSyncingGoogle(false);
                  setTimeout(() => setShowNotification(null), 4000);
                }
              }}
              disabled={syncingGoogle}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {syncingGoogle ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                </svg>
              )}
                Sync Google
              </button>
            )}
            <button
              onClick={() => {
                setEditingReview(null);
                setFormData({
                  name: "",
                  email: "",
                  rating: 5,
                  comment: "",
                  status: "approved",
                  source: "website",
                  created_at: new Date().toISOString().slice(0, 16),
                });
                setShowAddModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-accent text-white rounded-xl font-medium text-sm hover:bg-accent/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Review
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: "Total", value: stats.total, color: "bg-primary" },
            { label: "Approved", value: stats.approved, color: "bg-green-500" },
            { label: "Pending", value: stats.pending, color: "bg-yellow-500" },
            { label: "Google", value: stats.google, color: "bg-blue-500" },
            { label: "Website", value: stats.website, color: "bg-purple-500" },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-white rounded-xl p-4 border border-neutral-100"
            >
              <div className={`text-2xl font-bold ${stat.color.replace('bg-', 'text-')}`}>
                {stat.value}
              </div>
              <div className="text-xs text-neutral-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-neutral-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Search reviews..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-neutral-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm bg-white"
              >
                <option value="All">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="draft">Draft</option>
                <option value="rejected">Rejected</option>
              </select>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm bg-white"
              >
                <option value="All">All Sources</option>
                <option value="google">Google</option>
                <option value="website">Website</option>
              </select>
            </div>
          </div>
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-accent animate-spin" />
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="bg-white rounded-xl border border-neutral-100 p-12 text-center">
            <Star className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-primary mb-2">No reviews found</h3>
            <p className="text-sm text-neutral-500">
              {searchQuery || statusFilter !== "All"
                ? "Try adjusting your filters"
                : "Reviews will appear here once submitted"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredReviews.map((review) => (
              <motion.div
                key={review.id}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-xl border border-neutral-100 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-semibold">
                          {review.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-semibold text-primary text-sm">
                          {review.name}
                        </span>
                      </div>
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                          statusColors[review.status] || "bg-neutral-100 text-neutral-600"
                        }`}
                      >
                        {statusLabels[review.status] || review.status}
                      </span>
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                          sourceColors[review.source] || "bg-neutral-100 text-neutral-600"
                        }`}
                      >
                        {sourceLabels[review.source] || review.source}
                      </span>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-3.5 h-3.5 ${
                              star <= review.rating
                                ? "text-yellow-400 fill-yellow-400"
                                : "text-neutral-300"
                            }`}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Comment */}
                    <p className="text-sm text-neutral-600 leading-relaxed mb-3">
                      {review.comment}
                    </p>

                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-4 text-xs text-neutral-400">
                      {review.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {review.email}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(review.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {(review.status === "pending" || review.status === "draft") && (
                      <>
                        <button
                          onClick={() => handleStatusChange(review.id, "approved")}
                          className="flex items-center gap-1.5 px-3 py-2 bg-green-50 text-green-600 rounded-lg text-xs font-medium hover:bg-green-100 transition-colors"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleStatusChange(review.id, "rejected")}
                          className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Reject
                        </button>
                      </>
                    )}
                    {review.status === "approved" && (
                      <button
                        onClick={() => handleStatusChange(review.id, "draft")}
                        className="flex items-center gap-1.5 px-3 py-2 bg-neutral-100 text-neutral-600 rounded-lg text-xs font-medium hover:bg-neutral-200 transition-colors"
                      >
                        <Clock className="w-3.5 h-3.5" />
                        Move to Draft
                      </button>
                    )}
                    {review.status === "rejected" && (
                      <button
                        onClick={() => handleStatusChange(review.id, "approved")}
                        className="flex items-center gap-1.5 px-3 py-2 bg-green-50 text-green-600 rounded-lg text-xs font-medium hover:bg-green-100 transition-colors"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Approve
                      </button>
                    )}
                    <button
                      onClick={() => openEditReview(review)}
                      title="Edit review"
                      className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(review.id)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add Review Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => { setShowAddModal(false); setEditingReview(null); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-neutral-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-primary">{editingReview ? "Edit Review" : "Add Review"}</h2>
                <button
                  onClick={() => { setShowAddModal(false); setEditingReview(null); }}
                  className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-neutral-400" />
                </button>
              </div>

              <form onSubmit={handleAddReview} className="p-6 space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, name: e.target.value }))
                      }
                      placeholder="Client name"
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Email <span className="text-neutral-400">(optional)</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, email: e.target.value }))
                      }
                      placeholder="client@email.com (optional)"
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    />
                  </div>
                </div>

                {/* Rating */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Rating <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, rating: star }))
                        }
                        className="focus:outline-none transition-transform hover:scale-110"
                      >
                        <Star
                          className={`w-7 h-7 ${
                            star <= formData.rating
                              ? "text-yellow-400 fill-yellow-400"
                              : "text-neutral-300"
                          }`}
                        />
                      </button>
                    ))}
                    <span className="text-sm text-neutral-500 ml-2">
                      {formData.rating} out of 5
                    </span>
                  </div>
                </div>

                {/* Comment */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Review <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={formData.comment}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, comment: e.target.value }))
                    }
                    placeholder="Write the review..."
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm resize-none"
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Date <span className="text-neutral-400 font-normal">(optional)</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <input
                      type="datetime-local"
                      value={formData.created_at}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, created_at: e.target.value }))
                      }
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    />
                  </div>
                  <p className="text-xs text-neutral-400 mt-1">
                    Leave empty to use current date
                  </p>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, status: e.target.value }))
                    }
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm bg-white"
                  >
                    <option value="approved">Approved (show immediately)</option>
                    <option value="pending">Pending (requires approval)</option>
                    <option value="draft">Draft (not published)</option>
                  </select>
                </div>

                {/* Source */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Source
                  </label>
                  <select
                    value={formData.source}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, source: e.target.value }))
                    }
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm bg-white"
                  >
                    {sourceOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowAddModal(false); setEditingReview(null); }}
                    className="flex-1 px-4 py-2.5 border border-neutral-200 text-neutral-600 rounded-xl font-medium text-sm hover:bg-neutral-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      formSubmitting ||
                      !formData.name ||
                      !formData.comment
                    }
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-accent text-white rounded-xl font-medium text-sm hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {formSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      editingReview ? "Save Changes" : "Add Review"
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl w-full max-w-sm p-6"
            >
              <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-primary text-center mb-2">
                Delete Review?
              </h3>
              <p className="text-sm text-neutral-500 text-center mb-6">
                This action cannot be undone. The review will be permanently removed.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-4 py-2.5 border border-neutral-200 text-neutral-600 rounded-xl font-medium text-sm hover:bg-neutral-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium text-sm hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
